/**
 * Shared scaffolding for the four workflow queue pages.
 *
 * The pages differ in the stage they drive, not in how they behave: fetch a
 * queue, surface the server's own rejection text, act, refetch. Keeping that
 * shape in one place is what stops four near-identical copies drifting apart.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { errorMessage } from '@/api/client';
import { Modal } from './Modal';
import { Spinner } from './Spinner';

/**
 * Every timestamp crossing the API is epoch milliseconds — list queries select
 * through an `epoch_ms()` SQL helper so they agree with `claims.get`.
 *
 * This still accepts a string, because a query that loses its `epoch_ms()`
 * wrapper would otherwise render "Invalid Date" in a claim's history rather
 * than failing anywhere anyone would notice.
 */
export function toTs(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function ErrorNote({ message }: { message: string | null | undefined }): ReactNode {
  if (!message) return null;
  return (
    <div className="form-error" role="alert">
      {message}
    </div>
  );
}

export function QueuePanel({
  title,
  count,
  actions,
  children,
}: {
  title: string;
  count?: number;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="panel">
      <h3>
        <span className="title">
          {title}
          {count === undefined ? null : <span className="tag-chip">{count}</span>}
        </span>
        {actions ? <div className="actions">{actions}</div> : null}
      </h3>
      {children}
    </div>
  );
}

export interface QueueState<Row> {
  rows: Row[] | null;
  error: string | null;
  reload: () => Promise<void>;
  setError: (message: string | null) => void;
}

/** `load` must be referentially stable — wrap it in useCallback at the call site. */
export function useQueue<Row>(load: () => Promise<Row[]>): QueueState<Row> {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    try {
      const next = await load();
      setRows(next);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [load]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { rows, error, reload, setError };
}

export interface ActionDialogProps {
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Renders the confirm button in the danger style. */
  danger?: boolean;
  busy?: boolean;
  disabled?: boolean;
  error?: string | null;
  secondary?: ReactNode;
  children: ReactNode;
}

export function ActionDialog({
  title,
  confirmLabel,
  onConfirm,
  onCancel,
  danger = false,
  busy = false,
  disabled = false,
  error,
  secondary,
  children,
}: ActionDialogProps): ReactNode {
  return (
    <Modal
      title={title}
      // Closing mid-request would leave the user unsure whether it landed.
      onClose={busy ? () => undefined : onCancel}
      footer={
        <>
          {secondary}
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={busy || disabled}
          >
            {busy ? <Spinner /> : null} {confirmLabel}
          </button>
        </>
      }
    >
      <ErrorNote message={error} />
      {children}
    </Modal>
  );
}

/**
 * Runs one workflow call, holding the busy flag and keeping the server's own
 * 4xx wording. Business rejections are the point of these screens — replacing
 * them with "something went wrong" would hide the rule that was broken.
 */
export function useAction(): {
  busy: boolean;
  error: string | null;
  setError: (message: string | null) => void;
  run: (fn: () => Promise<void>) => Promise<boolean>;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async (fn: () => Promise<void>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, run };
}

/** Parses a money input, rejecting the empty string and NaN that `Number('')` hides. */
export function parseAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/** Matches the server's tolerance so the two sides never disagree by a cent. */
export const AMOUNT_EPSILON = 0.001;

export function todayIso(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
}
